import {chain} from 'lodash';
import React, {Component} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  CalendarsPanelId,
  EmlPanelId,
  MessagePanelId,
  ViewerPanelId,
} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {VisualParticipantModel} from '../../../../../models/api/visualParticipantModel';
import {CalendarInviteViewerParticipantToken} from './calendarInviteViewerParticipantToken';

const maximumTokensToDisplay = 50;

/*
 * Props.
 */

export interface CalendarInviteViewerParticipantsProps {
  panelId: CalendarsPanelId | ViewerPanelId | MessagePanelId | EmlPanelId;
  visualParticipants: ReadonlyArray<VisualParticipantModel>;
}

/*
 * Style.
 */

const StyledParticipantsDiv = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: -6px;
`;

const StyledMoreButton = styled(ChromelessButton)`
  color: ${(p) => p.theme.interactiveText};
  font-weight: ${fontWeights.medium};
  line-height: 22px;

  &:hover {
    color: ${(p) => p.theme.interactiveTextHover};
  }
`;

const StyledEvenMoreDiv = styled.div`
  padding: 3px 6px;
  margin: 0 6px 6px 0;

  background-color: ${(p) => p.theme.greys.shade30};
  border-radius: 4px;
`;

/*
 * Component.
 */

interface CalendarInviteViewerParticipantsState {
  areParticipantsExpanded: boolean;
}

export class CalendarInviteViewerParticipants extends Component<
  CalendarInviteViewerParticipantsProps,
  CalendarInviteViewerParticipantsState
> {
  constructor(props: CalendarInviteViewerParticipantsProps) {
    super(props);
    this.state = {areParticipantsExpanded: false};
  }

  /*
   * Event handlers.
   */

  private readonly toggleAreParticipantsExpanded = () => {
    this.setState(({areParticipantsExpanded}) => ({areParticipantsExpanded: !areParticipantsExpanded}));
  };

  render() {
    const participantCount = this.props.visualParticipants.length;
    const {areParticipantsExpanded} = this.state;
    const {panelId} = this.props;
    const numberOfTokensToDisplay = findNumberOfTokensToDisplay(participantCount, areParticipantsExpanded);

    return (
      <StyledParticipantsDiv>
        {this.renderParticipantTokens(panelId, numberOfTokensToDisplay)}
        {this.maybeRenderEvenMoreParticipantsDiv()}
        {this.maybeRenderMoreButton(numberOfTokensToDisplay)}
      </StyledParticipantsDiv>
    );
  }

  private renderParticipantTokens(
    panelId: CalendarsPanelId | ViewerPanelId | MessagePanelId | EmlPanelId,
    numberOfTokensToDisplay: number,
  ) {
    return chain(this.props.visualParticipants)
      .take(
        numberOfTokensToDisplay > maximumTokensToDisplay ? maximumTokensToDisplay : numberOfTokensToDisplay,
      )
      .map((vp) => (
        <CalendarInviteViewerParticipantToken key={vp.contact.uid} panelId={panelId} visualParticipant={vp} />
      ))
      .value();
  }

  private maybeRenderEvenMoreParticipantsDiv() {
    const participantCount = this.props.visualParticipants.length;
    const {areParticipantsExpanded} = this.state;

    if (participantCount <= maximumTokensToDisplay || !areParticipantsExpanded) {
      return null;
    }

    return (
      <StyledEvenMoreDiv>
        <Trans id="invite-participants-evenmore">+ {participantCount - maximumTokensToDisplay} more</Trans>
      </StyledEvenMoreDiv>
    );
  }

  private maybeRenderMoreButton(numTokensToDisplay: number) {
    const participantCount = this.props.visualParticipants.length;
    if (participantCount <= 7) {
      return null;
    }

    return (
      <StyledMoreButton onClick={this.toggleAreParticipantsExpanded}>
        {this.renderMoreButtonContent(participantCount - numTokensToDisplay)}
      </StyledMoreButton>
    );
  }

  private renderMoreButtonContent(count: number) {
    return this.state.areParticipantsExpanded ? (
      <Trans id="invite-participants-seeless">see less</Trans>
    ) : (
      <Trans id="invite-participants-seemore">see more ({count})</Trans>
    );
  }
}

/*
 * Helpers.
 */

// Display of participant tokens depends on how many participants there are:
// Less than 8: display all
// 8: display 6 and collapse 2, show a "see more" button
// >8: display 7 and collapse the rest, show a "see more" button
// >50: show 51st and beyond participants as simply "+ X more"
function findNumberOfTokensToDisplay(participantCount: number, areParticipantsExpanded: boolean) {
  if (participantCount < 8 || areParticipantsExpanded) {
    return participantCount;
  }

  if (participantCount === 8) {
    return 6;
  }

  return 7;
}
